// Month and weekday names and numbers in any language, from Intl.
//
// A lobby browser once had no Azerbaijani data and printed "Wed" and "M09": so a
// language the runtime does not list (supportedLocalesOf) uses the tables below,
// and any other language without data uses English.

type Fallback = 'en' | 'az' | 'ru'

// Sunday first, matching Date.getDay(). Russian months in the genitive, the form a date takes.
const WEEKDAYS: Record<Fallback, readonly string[]> = {
  en: ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
  ru: ['воскресенье', 'понедельник', 'вторник', 'среда', 'четверг', 'пятница', 'суббота'],
  az: ['bazar', 'bazar ertəsi', 'çərşənbə axşamı', 'çərşənbə', 'cümə axşamı', 'cümə', 'şənbə'],
}
const WEEKDAYS_SHORT: Record<Fallback, readonly string[]> = {
  en: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'],
  ru: ['Вс', 'Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб'],
  az: ['B.', 'B.e.', 'Ç.a.', 'Ç.', 'C.a.', 'C.', 'Ş.'],
}
const MONTHS: Record<Fallback, readonly string[]> = {
  en: ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'],
  ru: ['января', 'февраля', 'марта', 'апреля', 'мая', 'июня', 'июля', 'августа', 'сентября', 'октября', 'ноября', 'декабря'],
  az: ['yanvar', 'fevral', 'mart', 'aprel', 'may', 'iyun', 'iyul', 'avqust', 'sentyabr', 'oktyabr', 'noyabr', 'dekabr'],
}
const MONTHS_ALONE: Record<Fallback, readonly string[]> = {
  en: MONTHS.en,
  ru: ['январь', 'февраль', 'март', 'апрель', 'май', 'июнь', 'июль', 'август', 'сентябрь', 'октябрь', 'ноябрь', 'декабрь'],
  az: MONTHS.az,
}
const MONTHS_SHORT: Record<Fallback, readonly string[]> = {
  en: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
  ru: ['янв', 'фев', 'мар', 'апр', 'мая', 'июн', 'июл', 'авг', 'сен', 'окт', 'ноя', 'дек'],
  az: ['yan', 'fev', 'mar', 'apr', 'may', 'iyn', 'iyl', 'avq', 'sen', 'okt', 'noy', 'dek'],
}

const supported = new Map<string, boolean>()

/** Whether this runtime has locale data for lang. */
export function intlKnows(lang: string): boolean {
  let known = supported.get(lang)
  if (known === undefined) {
    try {
      known = Intl.DateTimeFormat.supportedLocalesOf([lang]).length > 0
    } catch {
      known = false
    }
    supported.set(lang, known)
  }
  return known
}

const fallbackOf = (lang: string): Fallback => (lang === 'az' || lang === 'ru' ? lang : 'en')

const names = new Map<string, readonly string[]>()

function fromIntl(lang: string, kind: string, count: number, options: Intl.DateTimeFormatOptions, at: (i: number) => number, part: Intl.DateTimeFormatPartTypes): readonly string[] {
  const id = `${lang}|${kind}`
  let list = names.get(id)
  if (!list) {
    const format = new Intl.DateTimeFormat(lang, { ...options, timeZone: 'UTC' })
    list = Array.from({ length: count }, (_, i) => format.formatToParts(at(i)).find((p) => p.type === part)?.value ?? '')
    names.set(id, list)
  }
  return list
}

export type MonthForm = 'long' | 'alone' | 'short'

/** A month's name: long as in a date ("15 сентября"), alone as a heading ("сентябрь"), short. */
export function monthName(lang: string, month0: number, form: MonthForm = 'long'): string {
  const table = form === 'short' ? MONTHS_SHORT : form === 'alone' ? MONTHS_ALONE : MONTHS
  const base = fallbackOf(lang.split('-')[0]!.toLowerCase())
  const ownTable = base === lang.split('-')[0]!.toLowerCase()
  // Full names in en/az/ru never come from Intl: a browser without az data drew "2 M11 2026".
  if ((ownTable && form !== 'short') || !intlKnows(lang)) return table[base][month0] ?? ''
  const options: Intl.DateTimeFormatOptions =
    form === 'alone' ? { month: 'long' } : { day: 'numeric', month: form === 'short' ? 'short' : 'long' }
  const name = fromIntl(lang, `month-${form}`, 12, options, (i) => Date.UTC(2026, i, 15), 'month')[month0] ?? ''
  return name && !/\d/.test(name) ? name : (table[base][month0] ?? '')
}

/** A weekday's name, Sunday = 0 as Date.getDay(). */
export function weekdayName(lang: string, weekday0: number, form: 'long' | 'short' = 'long'): string {
  if (!intlKnows(lang)) return (form === 'short' ? WEEKDAYS_SHORT : WEEKDAYS)[fallbackOf(lang)][weekday0] ?? ''
  // 4 January 2026 is a Sunday.
  return fromIntl(lang, `weekday-${form}`, 7, { weekday: form }, (i) => Date.UTC(2026, 0, 4 + i), 'weekday')[weekday0] ?? ''
}

/** A number as lang writes it (grouping, decimal mark). */
export function formatNumber(lang: string, n: number, options?: Intl.NumberFormatOptions): string {
  try {
    return new Intl.NumberFormat(intlKnows(lang) ? lang : 'en', options).format(n)
  } catch {
    return String(n)
  }
}
